import Controller, { OnBeforeFetch, OnBeforeSubmit, OnEdit, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableSubmitEvent, EditEvent, FetchEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Inject } from '@odinjs/odin';
import { FetchAPI, FormAPI, PhoneNumberAPI } from '@philips-internal/oswald-ui-components';
import { HttpResponse, HttpService, LanguageService } from '@philips-internal/oswald-ui-core/lib/services';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { ToastResponse } from '../../interfaces/ToastResponse';
import { showToastNotificationMessage } from '../../utils/toast-notification-utils';
import { Country } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import UserContext from '../../utils/user-context';
import { FragmentNames, PersonalContactAliases } from '../../codegen';
import ProfileModalUploadService from '../../services/profile-modal-upload-service';
import './index.css';
import ProfileBaseController from '../profile-base-controller';
import { HttpServiceError, PatientHttpService } from '../../services/patient-http-service';

interface State {
  data:  Record<string, FieldsValue>;
  oldValues: Record<string, FieldsValue>;
}

interface FormContactValues extends Record<string, Value> {
  additionalContact: FieldsValue[];
}

interface FieldTypes {
  dsValorNew: string | Value;
  dsValorOld: string | Value;
  nmAtributo: string;
  nrSequencia: number;
  tipoComplemento: string;
}

interface FieldsValue {
  dominio: {
    dsValorDominio: string;
    vlDominio: string;
  };
  nmContato: string;
  nrSequencia?: number;
  nrTelefone: string;
}

interface FormSubmitResult extends Record<string, Value> {
  campos: FieldTypes[];
  insertVal: InsertValProps[];
  updatedFields: string[];
}

interface FieldsRequireProof {
  dsDocumento: string;
  ieAttributo: string;
  nrSequenciaDoc: number;
}

interface InsertValProps {
  ieTipoComplemento: {
    dsValorDominio: string;
    vlDominio: string;
  };
  nmContato?: string;
  nrTelefone?: string;
}

enum ContextKeys {
  FORM_INITIAL_DATA = 'form_initial_data',
}

@Controller({ fragment: FragmentNames.PERSONAL_CONTACT })
export default class PersonalContactController extends ProfileBaseController {
  state: State = {
    data: {},
    oldValues: {},
  };

  @Inject(PersonalContactAliases.FETCH_CONTACT)
    fetchContactAPI!: FetchAPI;

  @Inject(PersonalContactAliases.FORM_CONTACT)
    formContactApi!: FormAPI;

  private language = LanguageService.getCurrentLanguage();

  @OnMount(PersonalContactAliases.FORM_CONTACT)
  onMountFormContact(event: MountEvent): void {
    const formApi = event.target as FormAPI;
    super.setContextVariablesForTaskModule();
    super.listenWhetherProfileEditingIsEnabled(formApi);
  }

  @OnEdit(PersonalContactAliases.FORM_CONTACT)
  onEditFormContact(event: EditEvent): void {
    const formApi = event.target as FormAPI;
    const formData = formApi.getData() as FormContactValues;

    this.context.set({ [ContextKeys.FORM_INITIAL_DATA]: formData });
  }

  @OnMount(PersonalContactAliases.ADDIONAL_CONTACT_PHONE_NUMBER)
  onMountAddionalContactPhoneNumber(event: MountEvent): void {
    const phoneNumber = event.target as PhoneNumberAPI;
    this.updateDefaultPhoneDdi(phoneNumber);
  }

  private updateDefaultPhoneDdi(phoneNumber: PhoneNumberAPI): void {
    const phoneCountry = this.language.split('-')[1].toLocaleUpperCase();
    phoneNumber.setCountry(phoneCountry as Country);
  }

  @OnBeforeFetch({ target: PersonalContactAliases.FETCH_CONTACT})
  handleBeforeFetchContactInformation(event: FetchEvent): void {
    const fetchContactInformation = event.target as FetchAPI;
    fetchContactInformation.setParameters({ personCode: UserContext.patientId });
  }

  @OnBeforeSubmit(PersonalContactAliases.FORM_CONTACT)
  onSubmitFormContact(event: AbortableSubmitEvent): void {
    const record = event.record as FormContactValues;
    const { campos, insertVal, updatedFields } = this.buildFormSubmitResult(record);

    if (campos.length === 0 && insertVal.length === 0) {
      return;
    }

    const requestBody = { campos, insertVal };
    HttpService.get(APPLICATION_ENDPOINTS.GET_FIELDS_REQUIRE_PROOF).then((resp: HttpResponse) => {
      const { body: bodyResponse } = resp as HttpResponse<FieldsRequireProof[]>;
      const fieldsRequireProof = bodyResponse && bodyResponse
        .filter(doc => updatedFields.some((updatedField) => updatedField === doc.ieAttributo.replace(/_/g, '')))
        .map(doc => ({
          attribute: doc.ieAttributo,
          key: doc.nrSequenciaDoc.toString(),
          required: true,
          value: doc.dsDocumento,
        }));

      if (fieldsRequireProof && fieldsRequireProof.length > 0) {
        ProfileModalUploadService.open({
          bodyToUpdate: requestBody,
          mandatoryAttachmentsFields: fieldsRequireProof,
          onConfirmUpload: e => this.sendUpdateRequest(e.record),
        });
      } else {
        this.sendUpdateRequest(requestBody);
      }
    });
  }

  buildFormSubmitResult(record: FormContactValues): FormSubmitResult {
    const campos: FieldTypes[] = [];
    const insertVal: InsertValProps[] = [];
    const { additionalContact: formRecord } = record;
    const { additionalContact: formData } = this.getFormContactInitialDataFromContext();
    const body = { campos, insertVal };
    const updatedFields: string[] = [];

    formRecord.forEach((__, index) => {
      const oldValues = formData[index];
      const newValues = formRecord[index];
      const nrSequencia = newValues.nrSequencia;
      let capturedInsertIndex = -1;

      Object.keys(newValues).forEach(keyName => {
        const key = keyName as keyof FieldsValue;
        if (oldValues[key] !== newValues[key]) {
          const oldV = oldValues[key];
          const newV = newValues[key];

          if(nrSequencia) {
            updatedFields.push(key.toUpperCase());
            body.campos.push({
              dsValorNew: newV ?? '',
              dsValorOld: oldV ?? '',
              nmAtributo: key,
              nrSequencia: nrSequencia,
              tipoComplemento: newValues.dominio.vlDominio,
            });
          } else if (capturedInsertIndex !== index) {
            capturedInsertIndex = index;
            body.insertVal.push({
              ieTipoComplemento: newValues.dominio,
              nmContato: newValues.nmContato,
              nrTelefone: newValues.nrTelefone,
            });
          }
        }
      });
    });

    return {
      updatedFields,
      ...body
    };
  }

  private getFormContactInitialDataFromContext(): FormContactValues {
    return this.context.get(ContextKeys.FORM_INITIAL_DATA) as FormContactValues;
  }

  sendUpdateRequest(body: Record<string, Value>): void {
    this.formContactApi.setLoading(true);
    PatientHttpService.post<ToastResponse>(APPLICATION_ENDPOINTS.POST_PROFILE_INFORMATION, { patientId: UserContext.patientId }, { body }).then((response) => {
      this.handleResponse(response);
      this.fetchContactAPI.forceFetch();
    }).catch(({ response }: HttpServiceError<ToastResponse>) => {
      this.handleResponse(response);
    }).finally(() => {
      this.formContactApi.setLoading(false);
    });
  }

  handleResponse(resp?: HttpResponse<ToastResponse>): void {
    if (resp?.body) {
      showToastNotificationMessage(resp.body);
    }
  }
}
